import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Button, Card, CardHeader } from '@truecare/ui';
import type {
  AdminCaregiverListItem,
  AdminCaregiverListResponse,
  ComplianceScanRunSummary,
} from '@truecare/schemas';
import { ApiError, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { adminBeforeLoad } from '../lib/admin-before-load';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';

/**
 * Phase 17 (bugfix — Vetting IA split) — Compliance ops page.
 *
 * Split off from the Vetting queue, which had grown to conflate two
 * distinct operator jobs: onboarding NEW applicants (that stayed on
 * /vetting) vs. keeping the EXISTING active roster compliant (this page).
 * Grouping is "by what the operator does" per the admin-nav philosophy.
 *
 * Two review sub-queues + the on-demand scan trigger:
 *   1. Pending compliance docs — `?hasPendingCompliance=true`. Any care
 *      professional with a TB / vaccination / GL-insurance / WSIB artifact
 *      in PENDING review (new applicants AND active pros who uploaded a
 *      renewal). Open the detail page to approve.
 *   2. Expiring / expired compliance — `?hasExpiringCompliance=true` (15b-6).
 *      APPROVED artifacts expiring within 30 days, or already EXPIRED. The
 *      daily 09:00 ET scan flips + notifies; this is the triage queue.
 *   3. "Run compliance scan now" — POST /admin/compliance/run-scan (15b-4),
 *      the same idempotent code path as the cron, for pilot ops / demos.
 *
 * Both sections are server-paginated; v1 scale fits the default 25-row page.
 */

export const Route = createFileRoute('/compliance')({
  beforeLoad: () => adminBeforeLoad('/compliance'),
  component: CompliancePage,
});

const CAREGIVER_COLUMNS: AdminTableColumn<AdminCaregiverListItem>[] = [
  {
    key: 'legalSurname',
    header: 'Name',
    sortable: false,
    render: (r) => `${r.preferredName ?? r.legalGivenName} ${r.legalSurname}`,
  },
  { key: 'email', header: 'Email', sortable: false },
  {
    key: 'declaredRoleCodes',
    header: 'Roles',
    width: 'w-32',
    sortable: false,
    render: (r) =>
      r.declaredRoleCodes.length > 0 ? r.declaredRoleCodes.join(', ') : '—',
  },
  {
    key: 'status',
    header: 'Status',
    width: 'w-32',
    sortable: false,
  },
];

function CompliancePage() {
  return (
    <AdminLayout>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="font-heading text-3xl text-forest md:text-4xl">
            Compliance
          </h1>
          <p className="font-body text-sm text-ink-muted">
            Keep the active roster compliant — documents waiting for review and
            credentials due to expire. Click any row to open it and follow up.
          </p>
        </div>
        <RunScanButton />
      </header>

      <PendingComplianceSection />
      <ExpiringComplianceSection />
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Run-scan trigger — POST /admin/compliance/run-scan (Phase 15b-4)
// ─────────────────────────────────────────────────────────────────────

function RunScanButton() {
  const qc = useQueryClient();
  const scan = useMutation<ComplianceScanRunSummary>({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/compliance/run-scan' as never, {} as never),
      )) as unknown as ComplianceScanRunSummary,
    onSuccess: () => {
      // Refresh both queues — a scan can flip APPROVED → EXPIRED.
      void qc.invalidateQueries({ queryKey: ['admin', 'compliance'] });
    },
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        onClick={() => scan.mutate()}
        loading={scan.isPending}
        data-testid="compliance-run-scan-button"
      >
        Run compliance scan
      </Button>
      {scan.data && (
        <p
          className="text-right text-xs text-ink-muted"
          data-testid="compliance-run-scan-summary"
        >
          Scanned {scan.data.artifactsScanned} · {scan.data.alertsFired} alert
          {scan.data.alertsFired === 1 ? '' : 's'} · {scan.data.autoExpiredCount}{' '}
          auto-expired · {scan.data.digestDispatched} digest
          {scan.data.digestDispatched === 1 ? '' : 's'}
          {scan.data.failuresCount > 0
            ? ` · ${scan.data.failuresCount} failed`
            : ''}
        </p>
      )}
      {scan.isError && (
        <p className="text-right text-xs text-danger" data-testid="compliance-run-scan-error">
          Scan failed:{' '}
          {scan.error instanceof ApiError ? scan.error.message : 'unknown error'}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section 1 — Pending compliance docs
// ─────────────────────────────────────────────────────────────────────

function PendingComplianceSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery<AdminCaregiverListResponse>({
    queryKey: ['admin', 'compliance', 'pending-docs', page],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers' as never, {
          params: { query: { hasPendingCompliance: true, page, pageSize: 25 } },
        } as never),
      )) as unknown as AdminCaregiverListResponse,
    placeholderData: keepPreviousData,
  });

  return (
    <Card padding="md" className="mt-6" data-testid="compliance-pending-card">
      <CardHeader
        title={`Pending compliance docs${
          query.data ? ` (${query.data.total})` : ''
        }`}
        description="Care professionals with any TB / GL insurance / WSIB compliance artifact in PENDING review. Includes ACTIVE professionals who uploaded a renewed document. Open the detail page to approve."
      />
      <AdminTable<AdminCaregiverListItem>
        columns={CAREGIVER_COLUMNS}
        rows={query.data?.items ?? []}
        isLoading={query.isPending}
        isError={query.isError}
        emptyMessage="No pending compliance docs to review."
        page={page}
        limit={25}
        totalCount={query.data?.total ?? 0}
        onPageChange={setPage}
        onRowClick={(r) => navigate({ to: '/caregivers/$id', params: { id: r.id } })}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section 2 — Expiring / expired compliance (15b-6)
// ─────────────────────────────────────────────────────────────────────

function ExpiringComplianceSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery<AdminCaregiverListResponse>({
    queryKey: ['admin', 'compliance', 'expiring', page],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers' as never, {
          params: { query: { hasExpiringCompliance: true, page, pageSize: 25 } },
        } as never),
      )) as unknown as AdminCaregiverListResponse,
    placeholderData: keepPreviousData,
  });

  return (
    <Card padding="md" className="mt-6" data-testid="compliance-expiring-card">
      <CardHeader
        title={`Expiring / expired compliance${
          query.data ? ` (${query.data.total})` : ''
        }`}
        description="Care professionals with an APPROVED compliance artifact expiring within 30 days, or one already EXPIRED. Open the detail page to follow up or suspend."
      />
      <AdminTable<AdminCaregiverListItem>
        columns={CAREGIVER_COLUMNS}
        rows={query.data?.items ?? []}
        isLoading={query.isPending}
        isError={query.isError}
        emptyMessage="No expiring or expired compliance docs."
        page={page}
        limit={25}
        totalCount={query.data?.total ?? 0}
        onPageChange={setPage}
        onRowClick={(r) => navigate({ to: '/caregivers/$id', params: { id: r.id } })}
      />
    </Card>
  );
}
